import { tileDataProp } from 'components/atoms/TileButton';
import AreaBarChart from 'components/molecules/AreaBarChart';
import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import {
    graphItemContainerClass,
    height,
    subHeaderClass,
    yAxisLabelClass,
} from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { Urls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchDrilldownData } from 'hooks/depletion/useFetchDrilldownData';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { depletionAreaLineParser } from 'parser/depletionAreaLineChartParser';
import { getDepletionExportData } from 'parser/graphToExcelDownloadParser';
import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getBaseUrl,
    getConfig,
} from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';

interface Props {
    volTile: tileDataProp;
    expandedItem: any;
    index: number;
    stringifiedFilter: string;
    loading: boolean;
    applyFilterError: any;
}

const labelMapping: any = {
    lineDataCy: 'Avg Price CY',
    lineDataYa: 'Avg Price YA',
};

const yAxisWidth = calculateFontSize(window.innerWidth, 60);
const chartHeight = calculateContainerHeight(3);

const TrendAreaChartContainer: FC<Props> = ({
    volTile,
    index,
    expandedItem,
    stringifiedFilter,
    loading,
    applyFilterError,
}) => {
    const mainLabel = volTile?.label + ' vs Net Price';
    const { allCheckedAttribute, timeSelectionLabel, timePeriodSelection } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const areaLineContainerRef = useRef<any>();

    const legendData = [
        { color: colorConstants.lightBlueAlt, label: volTile?.label },
        { color: colorConstants.darkBlueAlt, label: 'Avg Price CY', style: { height: '2px' } },
        { color: colorConstants.orangeAlt, label: 'Avg Price YA', style: { height: '2px' } },
    ];
    const currency = fetchCurrency(appConfig);

    const containerWidth = useContainerWidth({
        containerId: `depletion-area-chart-container`,
        buffer: yAxisWidth * 2 + 34 * 2,
    }); //subtracting graph width and padding on 2 sides

    const y2AxisLabel = `Avg Price/Unit (${currency})`;

    const variables = {
        feature_name: 'depletion',
        lag_list: commonConfig.lagList,
        data_type: volTile.dataKey,
        time_period: JSON.parse(timePeriodSelection),
        filters: JSON.parse(stringifiedFilter),
        agg_period: '',
    };
    const {
        data,
        error,
        loading: drillldownDataLoading,
    } = useFetchDrilldownData({
        method: 'post',
        queryConfig: { enabled: !loading && Boolean(expandedItem?.attributeExId) && Boolean(stringifiedFilter) },
        queryKey: ['depletion-bar-drilldown', variables],
        url: `${getBaseUrl()}${Urls.depletionDrilldownUrl}`,
        variables: {
            ...variables,
        },
        parserFunction: (data: any) => {
            if (data?.data?.length === 0) throw 'No Data';
            return depletionAreaLineParser(data?.data);
        },
    });

    const commonLoading = loading || drillldownDataLoading;

    const exportData = useMemo(() => {
        return getDepletionExportData(data?.data, volTile?.label, labelMapping.lineDataCy, labelMapping.lineDataYa);
    }, [data]);

    const commonError = error || applyFilterError;

    // IF ERROR RETURN ERROR MESSAGE
    if (commonError && !commonLoading) return <ErrorBox msg={String(commonError)} />;

    // IF LOADING RETURN LOADER
    if (commonLoading)
        return (
            <div style={{ height: height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );
    return (
        <section
            id="depletion-area-chart-container"
            ref={areaLineContainerRef}
            className={`${graphItemContainerClass}`}
        >
            <div className="flex justify-between items-center">
                <p className={subHeaderClass}>{mainLabel}</p>
                <div className="flex justify-center gap-2">
                    <CopyToClipBoard
                        //mixpanelEvent={() => trackEventMixpanel(GROWTH_ANALYSIS_CHART_COPY_CLICKED)}
                        refOfComponent={areaLineContainerRef}
                    />
                    {/* Header hardCoded for now */}
                    <DownloadExcel
                        title={'DepletionAreaChart'}
                        excelData={exportData.result}
                        headers={exportData.headers}
                    />
                </div>
            </div>
            <div className="flex justify-between my-3 items-center">
                <p className={`${yAxisLabelClass} w-[120px]`}>Sales {currency}</p>
                <Legend data={legendData} />
                <p className={`${yAxisLabelClass} w-[107px]`}>{y2AxisLabel}</p>
            </div>
            <div className="flex">
                {/* Y1 AXIS */}
                <Barchart
                    // padding={[0, 0, 0, 48]}
                    padding={[0, 0, 17, 0]}
                    barWidth={30}
                    xAxisConfig={false}
                    dataset={[]}
                    height={chartHeight}
                    loading={false}
                    chartContainer={`growth-depletion-y1`}
                    fields={['date', 'sales']}
                    //  minBound={scaleMinValue(40, tileChartData?.minVal)}
                    minBound={data?.minVal}
                    maxBound={data?.maxVal}
                    width={yAxisWidth}
                    colorKey={'type'}
                    yAxisLabelFormatter={(val: any) => {
                        return formatNumWithKMBAlt(val, 2, 2);
                    }}
                    // Yline
                    YGrid={false}
                />
                {/* CHART COMPONENT */}
                <div
                    id="trend-area-chart-container"
                    // style={{ width: chartConfig.width || 'auto' }}
                    className="overflow-x-auto w-full"
                >
                    <AreaBarChart
                        type="area"
                        width={containerWidth}
                        range={[0.001, 0.98]}
                        dataset={data?.data}
                        minBound={data?.minVal}
                        maxBound={data?.maxVal}
                        chartContainer="chart-container-depletion"
                        height={chartHeight}
                        loading={false}
                        fields={['date', 'areaData']}
                        color={colorConstants.lightBlueAlt}
                        xAxisLabelFormatter={(val: any) => {
                            return val;
                        }}
                        XGrid={false}
                        disableIntervalLabel
                        tooltipFormatter={(xAxis: any, val: any) => {
                            return {
                                name: volTile.label,
                                value: formatNumWithKMBAlt(val, 2, 2),
                            };
                        }}
                        groupedChartColor={[colorConstants.darkBlueAlt, colorConstants.orangeAlt]}
                        lineChartParams={{
                            field1: 'lineDataCy',
                            field2: 'lineDataYa',
                            minBound: data?.lineMinVal,
                            maxBound: data?.lineMaxVal,
                            tooltipFormatter: (xAxis: any, val: any) => {
                                return {
                                    name: labelMapping?.[xAxis],
                                    value: formatNumWithKMBAlt(val, 2, 2),
                                };
                            },
                        }}
                    />
                </div>
                {/* Y2 AXIS */}
                <div style={{ width: yAxisWidth }}>
                    <Barchart
                        padding={[0, 0, 19, 18]}
                        barWidth={30}
                        xAxisConfig={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`growth-depletion-y2`}
                        fields={['date', 'value']}
                        minBound={data?.lineMinVal}
                        maxBound={data?.lineMaxVal}
                        width={yAxisWidth}
                        colorKey={'type'}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val, 2, 2);
                        }}
                        YGrid={false}
                    />
                </div>
            </div>
            <p className={`flex justify-center relative ${yAxisLabelClass}`}>Date</p>
        </section>
    );
};

export default TrendAreaChartContainer;
